'use client';

import React, { useState } from 'react';
import { isBrowser } from '@/sharedTypes/browserUtils';

// Dynamic import of RecordRTC
import type RecordRTC from 'recordrtc';
let RecordRTCModule: typeof RecordRTC | null = null;
// eslint-disable-next-line
let isModuleLoading = true;

if (isBrowser()) {
  import('recordrtc')
    .then((module) => {
      RecordRTCModule = module.default;
      isModuleLoading = false;
    })
    .catch((err) => {
      console.error('Failed to load RecordRTC:', err);
      isModuleLoading = false;
    });
}

import fixWebmDuration from 'fix-webm-duration';

import './DebugRecorder.css';
import { observer } from 'mobx-react';
import DebugModelEntity from './DebugModel';
import { apiSwagger } from '../../shared/api';
import { ManagerEntity } from '@/shared/globalFunctions';
import { gMsg, notifyStatus } from '@/sharedTypes/coreTypes';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import { dpAssert } from '@/sharedTypes/helpers';
import { useManagerEntity } from '@/shared/managerHelpers';
import toast from '../ui/custom-toast';
import { useTranslation } from 'react-i18next';

const saveFile = false;

type RecorderGlobal = {
  recorder: RecordRTC | null;
};
const recorderGlobal: RecorderGlobal = {
  recorder: null,
};

const mimeType = 'video/webm';
const fileExtension = 'webm';

export const DebugRecorder = observer(() => {
  const manager = useManagerEntity();
  const player = manager?.player;
  const { t } = useTranslation();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordStartedDate, setRecordStartingDate] = useState<number>(0);

  const refreshUUIDs = () => {
    DebugModelEntity.refreshUUIDS();
  };

  return (
    <div
      style={{
        position: 'relative',
        top: '10%',
        right: '10px',
        zIndex: 9999,
        pointerEvents: 'auto',
      }}
    >
      {!isRecording ? (
        <button
          className='StartRecordingBtn DebugRecorder__btn'
          onClick={async () => {
            console.log('[DebugRecorder] Starting recording clicked');
            if (player) {
              console.log('[DebugRecorder] Starting recording...');
              await startRecording(setStream, recorderGlobal);
              setRecordStartingDate(Date.now());
              setIsRecording(true);
            }
          }}
        >
          Жучок
        </button>
      ) : (
        <button
          className='DebugRecorder__btn  StopRecordingBtn'
          onClick={async () => {
            if (player) {
              // if we still recording
              if (stream) {
                await stopRecording(
                  recorderGlobal,
                  setStream,
                  stream,
                  refreshUUIDs,
                  player,
                  recordStartedDate,
                  t
                );
              }
              setIsRecording(false);
            }
          }}
        >
          Выключить жучка
        </button>
      )}
    </div>
  );
});

const startRecording = async (
  setStream: React.Dispatch<React.SetStateAction<MediaStream | null>>,
  recorderGlobal: any
) => {
  console.log('[DebugRecorder] Starting recording process...');
  const startTime = performance.now();

  if (!RecordRTCModule) {
    console.error('[DebugRecorder] RecordRTC module not loaded');
    return;
  }

  try {
    console.log('[DebugRecorder] Requesting display media...');
    const videoStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
    });
    console.log('[DebugRecorder] Display media obtained');

    console.log('[DebugRecorder] Requesting microphone access...');
    const micStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    console.log('[DebugRecorder] Microphone access granted');

    console.log('[DebugRecorder] Setting up audio context...');
    const audioContext = new AudioContext();

    const audioIn_01 = audioContext.createMediaStreamSource(videoStream);
    const audioIn_02 = audioContext.createMediaStreamSource(micStream);

    const dest = audioContext.createMediaStreamDestination();

    audioIn_01.connect(dest);
    audioIn_02.connect(dest);

    videoStream.removeTrack(videoStream.getAudioTracks()[0]);
    videoStream.addTrack(dest.stream.getAudioTracks()[0]);

    setStream(videoStream);
    console.log('[DebugRecorder] Audio streams mixed and set up');

    console.log('[DebugRecorder] Initializing RecordRTC...');
    const r = new RecordRTCModule(videoStream, {
      type: 'video',
      timeSlice: 1000,
      mimeType: 'video/webm;codecs=vp9',
    });

    r.startRecording();
    recorderGlobal.recorder = r;
    console.log('[DebugRecorder] Recording started successfully');

    const duration = performance.now() - startTime;
    console.log(`[DebugRecorder] Total setup time: ${duration.toFixed(2)}ms`);
  } catch (error: any) {
    console.error('[DebugRecorder] Error during recording setup:', error);
    // Handle errors
    function showError(msg: string) {
      console.error(`[DebugRecorder] ${msg}`);
    }
    if (error.name === 'NotAllowedError') {
      showError('User denied permission for camera and/or microphone.');
    } else if (
      error.name === 'NotFoundError' ||
      error.name === 'DevicesNotFoundError'
    ) {
      showError('No camera and/or microphone found.');
    } else if (
      error.name === 'NotReadableError' ||
      error.name === 'TrackStartError'
    ) {
      showError('Camera and/or microphone is already in use or unavailable.');
    } else if (
      error.name === 'OverconstrainedError' ||
      error.name === 'ConstraintNotSatisfiedError'
    ) {
      const msg =
        'The constraints you specified could not be satisfied by the available devices.';
      showError(msg);
    } else if (error.name === 'InvalidStateError') {
      const msg =
        'Не удалось  получить доступ к аудио, пожалуйста выберите захват всего экрана и включите галочку предоставить доступ к аудиоданным';
      showError(msg);
    } else {
      const msg = 'Error accessing camera and/or microphone:';
      showError(msg);
    }
  }
};

async function stopRecording(
  recorderGlobal: RecorderGlobal,
  setStream: React.Dispatch<React.SetStateAction<MediaStream | null>>,
  stream: MediaStream | null,
  refreshUUIDs: () => void,
  player: any | undefined,
  recordStartedDate: number,
  t: any
) {
  console.log('[DebugRecorder] Stopping recording process...');
  const startTime = performance.now();

  ManagerEntity.spinnerMsg = 'saving debug info...';

  const RTCRecorder = recorderGlobal.recorder;

  if (RTCRecorder) {
    let duration: any = null;
    if (recordStartedDate) {
      duration = Date.now() - recordStartedDate;
      console.log(`[DebugRecorder] Recording duration: ${duration}ms`);
    }

    console.log('[DebugRecorder] Stopping recorder...');
    await RTCRecorder.stopRecording();

    if (player?.eventsRecorder.debugData) {
      console.log('[DebugRecorder] Saving debug data...');
      await saveDebugData(player?.eventsRecorder.debugData, t);
    }

    console.log('[DebugRecorder] Getting recording blob...');
    const blob = await RTCRecorder.getBlob();

    if (blob) {
      console.log(`[DebugRecorder] Blob size: ${blob?.size} bytes`);
      if (player) {
        if (recordStartedDate) {
          if (duration) {
            console.log('[DebugRecorder] Fixing webm duration...');
            fixWebmDuration(blob, duration, async function (fixedBlob) {
              console.log('[DebugRecorder] Saving video...');
              const saved = await saveVideo(
                fixedBlob,
                player.eventsRecorder.debugData.uuid
              );

              if (stream) {
                console.log('[DebugRecorder] Stopping media tracks...');
                stream.getTracks().forEach((track) => track.stop());
                setStream(null);
              }

              if (saveFile) {
                console.log('[DebugRecorder] Saving file locally...');
                const file = new File(
                  [fixedBlob],
                  'file-name.' + fileExtension,
                  {
                    type: mimeType,
                  }
                );
                dpAssert(RecordRTCModule, 'RecordRTCModule is null');
                RecordRTCModule.invokeSaveAsDialog(file);
              }

              refreshUUIDs();
              if (saved) {
                console.log('[DebugRecorder] Recording saved successfully');
                ManagerEntity.gMsg = new gMsg(
                  'Recording saved',
                  notifyStatus.success
                );
              } else {
                console.error('[DebugRecorder] Failed to save recording');
                ManagerEntity.gMsg = new gMsg(
                  'Failed to save recording',
                  notifyStatus.failed
                );
              }
              ManagerEntity.spinnerMsg = '';

              const totalDuration = performance.now() - startTime;
              console.log(
                `[DebugRecorder] Total stop process time: ${totalDuration.toFixed(
                  2
                )}ms`
              );
            });
          }
        }
      } else {
        console.error(
          '[DebugRecorder] Player not found when trying to save video'
        );
        ManagerEntity.gMsg = new gMsg(
          'Player not found when tried to save video',
          1
        );
        ManagerEntity.spinnerMsg = '';
      }
    } else {
      console.error('[DebugRecorder] Video blob not saved, there is no blob');
      ManagerEntity.gMsg = new gMsg("Video blob not saved, there' no blob", 1);
      ManagerEntity.spinnerMsg = '';
    }
  } else {
    console.error('[DebugRecorder] Recorder is undefined');
    ManagerEntity.gMsg = new gMsg('Recorded is undefined', 1);
    ManagerEntity.spinnerMsg = '';
  }
}

function serializeObject(obj: any) {
  const result: any = {
    ...obj,
  };
  result.events = {};
  for (const key in obj.events) {
    const k = key.toString();
    result.events[k] = obj.events[key];
  }
  return result;
}

async function saveVideo(blob: any, uuid: string) {
  console.log('[DebugRecorder] Starting video save process...');
  const startTime = performance.now();

  if (blob) {
    const formData = new FormData();
    formData.append('file', blob);
    formData.append('uuid', uuid);

    console.log('[DebugRecorder] Sending video to server...');
    const response = await apiSwagger.debugVideo.debugVideoCreate(
      formData as any
    );

    if (response.ok) {
      console.log('[DebugRecorder] Video saved successfully');
      const duration = performance.now() - startTime;
      console.log(`[DebugRecorder] Video save time: ${duration.toFixed(2)}ms`);
      return true;
    } else {
      console.error('[DebugRecorder] Failed to save video');
      return false;
    }
  }
  return false;
}

export async function saveDebugData(debugData: any, t: any) {
  console.log('[DebugRecorder] Starting debug data save process...');
  const startTime = performance.now();

  const sDebugData: any = serializeObject(debugData);
  try {
    const isEmpty = (obj: any) => {
      return Object.keys(obj).length === 0;
    };
    if (!isEmpty(debugData.events)) {
      console.log('[DebugRecorder] Sending debug data to server...');
      const response = await apiSwagger.debugEvents.debugEventsCreate(
        sDebugData
      );
      console.log('[DebugRecorder] Debug data save response:', response);

      const interval = setInterval(() => {
        console.log('[DebugRecorder] Triggering debug data saved event...');
        triggerEvent('debugDataSaved', response.data.uuid);
      }, 1000);

      setTimeout(() => {
        clearInterval(interval);
      }, 10000);

      if (response.ok) {
        console.log('[DebugRecorder] Debug data saved successfully');
        toast({
          title: t('toast.success_title'),
          description: `${t('toast.debug_data_saved_success')}: UUID - ${
            response.data.uuid
          }`,
          type: 'success',
          duration: 20000,
        });
        const duration = performance.now() - startTime;
        console.log(
          `[DebugRecorder] Debug data save time: ${duration.toFixed(2)}ms`
        );
        return true;
      } else {
        console.error('[DebugRecorder] Debug data failed to save');
        toast({
          title: t('toast.error_title'),
          description: t('toast.debug_data_save_error'),
          type: 'error',
        });
        return false;
      }
    } else {
      console.warn('[DebugRecorder] Attempted to save empty debug data');
      toast({
        title: t('toast.warning_title'),
        description: t('toast.debug_no_data_warning'),
        type: 'warning',
      });
      return false;
    }
  } catch (error) {
    console.error('[DebugRecorder] Error saving debug data:', error);
    toast({
      title: t('toast.error_title'),
      description: `${t('toast.debug_save_error_desc')}: ${error}`,
      type: 'error',
    });
    return false;
  }
}
